import type { ObjectData } from '@/domain';
import { replaceHtmlNewlines } from '@/utils/formatting';

const parsePositiveInt = (value: string | null): number | null => {
    const parsed = Number(value);
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const mapFacility = (facility: string): string | null => {
    switch (facility) {
        case 'Aircondition':
            return 'ac';
        case 'Balkong/terrasse':
            return 'balcony';
        case 'Peis/ildsted':
            return 'fireplace';
        case 'Mikrobølgeovn':
            return 'microwave';
        case 'Grill':
            return 'outdoorGrill';
        case 'Internettilgang':
            return 'wifi';
        case 'Kjøkken':
            return 'kitchen';
        case 'Svømmebasseng':
            return 'pool';
        case 'TV':
            return 'tv';
        default:
            return null;
    }
};

const mapAmenityFeatures = (facilities: string[] | null) => {
    if (!facilities) return null;
    const mappedFacilities = facilities.map(mapFacility).filter((name) => name !== null);
    if (mappedFacilities.length === 0) return null;
    return mappedFacilities.map((name) => ({ '@type': 'LocationFeatureSpecification' as const, name, value: true }));
};

export const generateStructuredData = (objectData: ObjectData): VacationRentalJsonLd | null => {
    // Fields required by google for a structured data VacationRental-object:
    // https://developers.google.com/search/docs/appearance/structured-data/vacation-rental#structured-data-type-definitions
    const { noOfBeds, adId, images, location, heading } = objectData;
    const occupancyValue = parsePositiveInt(noOfBeds);

    if (!heading || !occupancyValue || !images || !location) {
        return null;
    }

    const additionalType = objectData.propertyType ? mapPropertyType(objectData.propertyType) : null;
    const numberOfBedrooms = parsePositiveInt(objectData.noOfBedrooms);
    const numberOfBathroomsTotal = parsePositiveInt(objectData.noOfBathrooms);
    const amenityFeature = mapAmenityFeatures(objectData.facilities);

    return {
        '@context': 'https://schema.org',
        '@type': 'VacationRental',
        name: heading,
        identifier: String(adId),
        image: images.map((image) => image.src),
        latitude: location.latitude,
        longitude: location.longitude,
        ...(objectData.residenceDescription && { description: replaceHtmlNewlines(objectData.residenceDescription) }),
        ...(additionalType && { additionalType }),
        containsPlace: {
            '@type': 'Accommodation',
            occupancy: {
                '@type': 'QuantitativeValue',
                value: occupancyValue,
            },
            ...(numberOfBedrooms && { numberOfBedrooms }),
            ...(numberOfBathroomsTotal && { numberOfBathroomsTotal }),
            ...(amenityFeature && { amenityFeature }),
            ...(objectData.houseRules?.petsAllowed && { petsAllowed: true }),
            ...(objectData.houseRules?.smokingAllowed && { smokingAllowed: true }),
        },
        address: {
            '@type': 'PostalAddress',
            addressCountry: location.countryCode,
            addressLocality: location.locality,
        },
    };
};

type JsonLdBase = {
    '@context': 'https://schema.org';
    '@type': string;
};

type VacationRentalJsonLd = JsonLdBase & {
    '@type': 'VacationRental';
    name: string;
    identifier: string;
    image: string[];
    latitude: number;
    longitude: number;
    description?: string;
    additionalType?: string;
    containsPlace: {
        '@type': 'Accommodation';
        occupancy: {
            '@type': 'QuantitativeValue';
            value: number;
        };
        numberOfBedrooms?: number;
        numberOfBathroomsTotal?: number;
        amenityFeature?: { '@type': 'LocationFeatureSpecification'; name: string; value: boolean }[];
        petsAllowed?: boolean;
        smokingAllowed?: boolean;
    };
    address: {
        '@type': 'PostalAddress';
        addressCountry: string;
        addressLocality: string;
    };
};

const mapPropertyType = (propertyType: string): string | null => {
    switch (propertyType) {
        case 'Leilighet':
            return 'Apartment';
        case 'Hytte/Feriehjem':
            return 'Cabin';
        case 'Hus':
            return 'House';
        default:
            return null;
    }
};
